<template>

    <section class="mb-64">

        <component-header :description="description" :title="title">
        </component-header>

        <component :is="'style'" v-html="customStyles"></component>

        <div
            :id="jobsSlug"
            :x-data="`window.jobComponent(
            ${itemsToJSON},
            ${itemsPerPage},
            ${JSON.stringify(availableLocationFilters)},
            ${JSON.stringify(availableTypeFilters)},
            ${JSON.stringify(availableTagFilters)},
            ${JSON.stringify(availableFilterGroups)}
            )`"
            class="site__container"
            x-on:location-filter-selected="locationFilter = $event.detail.id; page = 1"
            x-on:tag-filter-selected="tagFilter = $event.detail.id; page = 1"
            x-on:type-filter-selected="typeFilter = $event.detail.id; page = 1"
        >

            <div
                :class="[ containerWidth ]"
                class="mx-auto"
            >
                <div
                    v-if="activeForLocale.length > 0 && (tagFilterEnabled || locationFilterEnabled || typeFilterEnabled)"
                    :class="[ 'w-full' ]"
                    class="text-body-sm mb-32">
                    <div class="flex flex-wrap space-x-10">
                        <div v-if="locationFilterEnabled" class="relative">
                            <button
                                class="focus:outline-none site__focus--outline-brand trans-ease hover:bg-primary-5 site__rounded--small border site__border--text-light px-16 h-40 flex items-center cursor-pointer"
                                x-on:click="locationFilterOpen = !locationFilterOpen;"
                            >
                                <svg-filter type="location"></svg-filter>
                                <span :class="styleFilterText"
                                      class="first-letter:uppercase mx-8"
                                >{{ $t('jobs.filter.filter_on_location') }}
                                </span>
                                <span
                                    :class="styleFilterIcon"
                                    :style="{ 'border-top-color' : textHSLA }"
                                    class="dropdown-triangle trans-ease"
                                    x-bind:class="{'transform -rotate-180': locationFilterOpen}"
                                ></span>
                            </button>

                            <ul
                                class="max-h-320 left-0 mt-8 bg-white border border-primary-5 absolute site__rounded--small overflow-x-hidden overflow-y-auto shadow pt-16 trans-ease z-10"
                                x-cloak
                                x-on:click.away="locationFilterOpen = false"
                                x-show="locationFilterOpen"
                                x-transition:enter="transition ease-out duration-300"
                                x-transition:enter-end="opacity-100 transform translate-y-0"
                                x-transition:enter-start="opacity-0 transform -translate-y-8"
                                x-transition:leave="transition ease-in duration-300"
                                x-transition:leave-end="opacity-0 transform -translate-y-8"
                                x-transition:leave-start="opacity-100 transform translate-y-0"
                                v-html="locationFilter"
                            ></ul>
                        </div>

                        <div v-if="typeFilterEnabled" class="relative">

                            <button
                                class="focus:outline-none site__focus--outline-brand trans-ease hover:bg-primary-5 site__rounded--small border site__border--text-light px-16 h-40 flex items-center cursor-pointer"
                                x-on:click="typeFilterOpen = !typeFilterOpen">
                                <svg-filter type="type"></svg-filter>
                                <span :class="styleFilterText"
                                      class="capitalize mx-8"
                                >{{ $t('jobs.filter.fulltime-parttime') }}
                                </span>
                                <span
                                    :class="styleFilterIcon"
                                    :style="{ 'border-top-color' : textHSLA }"
                                    class="dropdown-triangle trans-ease"
                                    x-bind:class="{'transform -rotate-180': typeFilterOpen}"
                                ></span>
                            </button>

                            <ul
                                class="max-h-320 left-0 mt-8 bg-white border border-primary-5 absolute site__rounded--small overflow-x-hidden overflow-y-auto shadow pt-16 trans-ease z-10"
                                x-cloak
                                x-on:click.away="typeFilterOpen = false"
                                x-show="typeFilterOpen"
                                x-transition:enter="transition ease-out duration-300"
                                x-transition:enter-end="opacity-100 transform translate-y-0"
                                x-transition:enter-start="opacity-0 transform -translate-y-8"
                                x-transition:leave="transition ease-in duration-300"
                                x-transition:leave-end="opacity-0 transform -translate-y-8"
                                x-transition:leave-start="opacity-100 transform translate-y-0"
                                v-html="typeFilter"
                            >
                            </ul>
                        </div>

                        <template v-if="filterGroupsEnabled">
                            <template v-for="group in availableFilterGroups" :key="group.id">
                                <div class="relative" :x-data="`{ group: ${JSON.stringify(group)} }`">
                                    <button class="focus:outline-none site__focus--outline-brand trans-ease
                                            hover:bg-primary-5 site__rounded--small border site__border--text-light
                                            px-16 h-40 flex items-center cursor-pointer"
                                            x-on:click="openFilterGroup(group.id)"
                                    >
                                    <svg class="block fill-current flex-none" width="22" height="16">
                                        <path d="m1 0c-.6 0-1 .4-1 1v2c0 .3.1.6.4.8l5.6 4.7v6.5c0 .4.2.8.6.9.1.1.3.1.4.1.3 0 .5-.1.7-.3l2-2c.2-.2.3-.4.3-.7v-4.5l5.6-4.7c.3-.2.4-.5.4-.8v-2c0-.6-.4-1-1-1z"/>
                                        <path d="m19 8h-2v3h-3v2h3v3h2v-3h3v-2h-3z"/>
                                    </svg>
                                    <span class="capitalize mx-8 ${this.styleFilterText}" x-text="group.alias"></span>
                                        <span style="'border-top-color' : ${this.textHSLA};"
                                              class="dropdown-triangle trans-ease ${this.styleFilterIcon}"
                                              x-bind:class="{'transform -rotate-180': groupFilterOpen[group.id]}"
                                        ></span>
                                    </button>
                                    <ul class="max-h-320 left-0 mt-8 bg-white border border-primary-5 absolute
                                        site__rounded--small overflow-x-hidden overflow-y-auto shadow
                                        pt-16 trans-ease z-10"
                                        x-cloak
                                        x-on:click.away="groupFilterOpen[group.id] = false"
                                        x-show="groupFilterOpen[group.id]"
                                        x-transition:enter="transition ease-out duration-300"
                                        x-transition:enter-end="opacity-100 transform translate-y-0"
                                        x-transition:enter-start="opacity-0 transform -translate-y-8"
                                        x-transition:leave="transition ease-in duration-300"
                                        x-transition:leave-end="opacity-0 transform -translate-y-8"
                                        x-transition:leave-start="opacity-100 transform translate-y-0"
                                        x-html="filterGroupTemplate(group.id)"
                                    >
                                    </ul>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>

                <job-empty-state :description="$t('jobs.empty_state.description_jobsite')"
                                 :title="$t('jobs.empty_state.title')"
                                 x-show="items.length > 0 && !filteredItemsOnThisPage().length > 0">
                </job-empty-state>

                <div v-if="style === 'grid'">
                    <ul
                        class="grid gap-16"
                        :class="gridClasses"
                        v-html="jobTemplate"
                    ></ul>

                    <div class="flex justify-center my-16" x-show="totalPages() > 1" v-html="pagination"></div>
                </div>

                <div v-if="style === 'rows'">
                    <ul
                        class="site__rounded--medium overflow-hidden mb-16 w-full"
                        v-html="jobTemplate"
                    />

                    <div class="flex justify-center mb-32" x-show="totalPages() > 1" v-html="pagination"></div>

                    <div v-if="spontaneous" class="flex flex-col items-center">
                        <div :class="[ textSize ]" class="text-center font-semibold mb-24">
                            {{ $t('companypage.spontaneous_cta') }}
                        </div>
                        <a
                            :class="[ buttonTextSize ]"
                            :href="route('jobsite.' + locale + '.job', { job: spontaneous.slug[locale] })"
                            class="site__rounded--large site__bg--brand site__color--brand-contrast px-16 py-8 pb-12 font-semibold flex items-center"
                        >
                            {{ $t('companypage.apply_confirm') }}
                        </a>
                    </div>
                </div>
            </div>

        </div>

    </section>

</template>

<script>
import {ucfirst} from "../../helpers";
import {mapState} from 'pinia';
import { useCommonStore } from '../../state/common';
import { useBrandingStore } from '../../state/branding'
import { useRepo } from "pinia-orm";
import ComponentHeader from './ComponentHeader.vue';
import JobEmptyState from './JobEmptyState.vue';
import {route, responsive} from '../../helpers';
import SvgFilter from './icons/Filter.vue';
import _findIndex from 'lodash/findIndex';
import _kebabCase from 'lodash/kebabCase';
import Styles from './Styles.vue';
import FilterGroup from "../../../app/models/FilterGroup";
import JobFilter from "../../../app/models/JobFilter";

export default {
    components: {
        Styles,
        'component-header': ComponentHeader,
        'svg-filter': SvgFilter,
        'job-empty-state': JobEmptyState,
    },
    props: {
        component: {
            required: true,
        },
    },
    data() {
        return {
            itemsPerPage: this.component.pagination || 999, // If set use the pagination otherwise a high number so it doesn't occur
        };
    },
    computed: {
        ...mapState(useCommonStore, ['locale']),
        ...mapState(useBrandingStore, ['preview', 'allLocations', 'brandHSLA', 'brandContrastHEX', 'textHSLA']),
        withImages() {
            return !['grid_without_images', 'rows_without_images'].includes(this.component.style);
        },
        style() {
            return ['grid', 'grid_without_images'].includes(this.component.style) ? 'grid' : 'rows';
        },
        title() {
            if (!this.component.title.hasOwnProperty(this.locale)) {
                return null;
            }
            return this.component.title[this.locale];
        },
        description() {
            if (!this.component.description.hasOwnProperty(this.locale)) {
                return null;
            }
            return this.component.description[this.locale];
        },
        spontaneous() {
            return this.component.jobs.find(job => !!job.is_spontaneous && !!job.active[this.locale]);
        },
        activeForLocale() {
            return this.component.jobs.filter(job => {
                return job.active && job.active[this.locale] == true && !job.is_spontaneous;
            }).sort((a, b) => a.sequence > b.sequence ? 1 : -1);
        },
        itemsToJSON() {
            return JSON.stringify(this.activeForLocale.map(job => ({
                id: job.id,
                title: this.translated(job.title, null, true),
                button: this.getButtonText(job),
                url: route('jobsite.' + this.locale + '.job', {job: job.slug[this.locale]}),
                image_url: job.image_url,
                location: this.getJobLocation(job),
                types: this.getJobTypes(job),
                filters: {
                    filterIds: job.filters.map(f => f.id),
                    locationId: job.location ? job.location.id : null,
                    types: job.type,
                },
            })));
        },
        jobTemplate() {
            return (this.style === 'rows') ? this.rowsTemplate : this.gridTemplate;
        },
        rowsTemplate() {
            return `
                <template x-for="(job, index) in filteredItemsOnThisPage()">
                    <li>
                        <a
                            x-bind:href="job.url"
                            class="overflow-hidden flex job-link group ${this.lightFlexDirection}"
                            x-bind:class="index % 2 ? 'bg-transparent' : 'site__bg--brand-10'"
                        >
                            ${this.withImages ? `
                                <div class="flex-none overflow-hidden ${this.lightImageWidth}">
                                    <div
                                        class="w-full h-full bg-cover bg-center job-image trans-ease-slow group-hover:scale-110 ease-in-out"
                                        x-bind:style="'background-image: url(' + job.image_url + '&w=445&fit=max);'"
                                    ></div>
                                </div>
                            ` : ''}
                            <div
                                class="${this.lightFlex} p-40 items-center flex-grow ${this.withImages ? this.lightContentWidth : ''}"
                            >
                                <div class="mr-auto flex flex-col">
                                    <span class="site__color--text-70 text-body">
                                        <span x-show="job.location" class="mr-16" x-text="job.location"></span>
                                        <span x-text="job.type"></span>
                                    </span>
                                    <span
                                        class="text-heading-2 font-semibold line-clamp-2 group-hover:-translate-y-4 ease-in-out transition-transform"
                                        style="word-break: break-word"
                                        x-text="job.title"
                                    ></span>
                                </div>
                                <div class="mt-16 ${this.lightButtonPadding}">
                                    <div
                                        class="first-letter:uppercase font-semibold job-button whitespace-nowrap branded trans-ease site__rounded--large site__color--brand-contrast site__bg--brand px-16 py-8 pb-12 flex items-center ${this.buttonTextSize}"
                                        x-text="job.button"
                                    >
                                        ${this.$t('jobsite.view_opening')}
                                    </div>
                                </div>
                            </div>
                        </a>
                    </li>
                </template>
            `;
        },
        gridTemplate() {
          return `
                <template x-for="(job, index) in filteredItemsOnThisPage()">
                    <li class="${this.gridItemHeight + ' ' + this.gridItemWidth}">
                        <a
                            x-bind:href="job.url"
                            class="h-full job-link block relative site__rounded--medium site__bg--brand overflow-hidden group"
                        >
                        <span class="site__color--brand-contrast flex flex-col h-full">
                            ${this.withImages ? `
                                <div class="w-full h-164 overflow-hidden">
                                    <div
                                        class="w-full h-full bg-cover bg-center job-image trans-ease-slow group-hover:scale-110 ease-in-out"
                                        x-bind:style="'background-image: url(' + job.image_url + '&w=445&fit=max);'"
                                    ></div>
                                </div>
                            ` : ''}
                            <span
                                class="flex-grow flex flex-col justify-between ${this.heavyPadding + ' ' + (this.withImages ? this.heavyPaddingWithImages : '')}"
                            >
                                <span class="mb-8 flex flex-col">
                                    <span class="mb-8 text-body">
                                        <span
                                            class="mr-8"
                                            x-show="job.location"
                                            x-text="job.location"
                                        ></span>
                                        <span
                                            x-show="job.type"
                                            x-text="job.type"
                                        ></span>
                                    </span>
                                    <span
                                        class="text-heading-2 font-semibold line-clamp-2 group-hover:-translate-y-4 ease-in-out transition-transform"
                                        x-text="job.title"
                                    ></span>
                                </span>
                                <span class="flex items-center job-button font-semibold">
                                    <span
                                        class="ml-auto mr-4 text-body"
                                        x-text="job.button"
                                    ></span>
                                    <svg
                                        class="w-18 h-18 trans-ease block fill-current group-hover:translate-x-4 ease-in-out transition-transform"
                                        viewBox="0 0 16 13"
                                        height="13"
                                        width="16"
                                    >
                                        <path d="m819.3 2635-1.4 1.35821 4.3 4.17164h-12.2v1.9403h12.2l-4.3 4.17164 1.4 1.35821 6.7-6.5z" transform="translate(-810 -2635)"/>
                                    </svg>
                                </span>
                            </span>
                        </span>
                        </a>
                    </li>
                </template>
                ${this.spontaneous && this.spontaneous.active[this.locale] ? `
                    <li
                        id="spontaneous"
                        class="${this.gridItemHeight + ' ' + this.gridItemWidth}"
                    >
                        <a
                            href="${route('jobsite.' + this.locale + '.job', {job: this.spontaneous.slug[this.locale]})}"
                            class="h-full job-link block relative site__rounded--medium site__bg--text site__color--text-contrast overflow-hidden group"
                        >
                            <span class="h-full flex flex-col justify-between ${this.heavyPadding}">
                                <span class="text-heading-2 font-semibold  group-hover:translate-y-4 ease-in-out transition-transform">
                                    ${this.$t('companypage.spontaneous_cta')}
                                </span>
                                <span>
                                    <div class="w-full h-2 site__bg--brand mb-8"></div>
                                    <span class="flex items-center job-button">
                                        <span class="ml-auto mr-16">
                                            ${this.$t('companypage.apply_confirm')}
                                        </span>
                                        <svg
                                          class="w-18 h-18 trans-ease block fill-current group-hover:translate-x-4 ease-in-out transition-transform"
                                          viewBox="0 0 16 13"
                                          height="13"
                                          width="16"
                                        >
                                            <path d="m819.3 2635-1.4 1.35821 4.3 4.17164h-12.2v1.9403h12.2l-4.3 4.17164 1.4 1.35821 6.7-6.5z" transform="translate(-810 -2635)"/>
                                        </svg>
                                    </span>
                                </span>
                            </span>
                        </a>
                    </li>
                ` : ''}
            `;
        },
        pagination() {
            return `
                <template x-for="p in loopPages()">
                    <div x-on:click="paginate(p);"
                         class="site__rounded--large cursor-pointer h-40 w-40 flex items-center justify-center mx-4"
                         x-bind:class="page === p ? 'site__color--brand-contrast site__bg--brand' : 'border-2'" x-text="p">
                    </div>
                </template>
            `;
        },
        locationFilter() {
            return `
                <template x-for="filter in locations">
                    <li class="mb-8 -mt-8 flex items-center pl-12 pr-16 py-4 hover:bg-primary-5" x-on:click="locationFilterSelected(filter)">
                        <input type="checkbox" class="mr-10" :checked="filter.id === '' && !locationFilter.length ? true : containsLocation(filter)">
                        <span class="block cursor-pointer whitespace-nowrap" x-text="filter.alias"></span>
                    </li>
                </template>
            `;
        },
        typeFilter() {
            return `
                <template x-for="filter in types">
                    <li class="mb-8 -mt-8 flex items-center pl-12 pr-16 py-4 hover:bg-primary-5" x-on:click="typeFilterSelected(filter)">
                        <input type="checkbox" class="mr-10" :checked="filter.id === '' && !typeFilter.length ? true : containsType(filter)">
                        <span class="block cursor-pointer whitespace-nowrap" x-text="filter.alias"></span>
                    </li>
                </template>
            `;
        },
        tagFilter() {
            return `
                <template x-for="filter in tags">
                    <li class="mb-8 -mt-8 flex items-center pl-12 pr-16 py-4 hover:bg-primary-5" x-on:click="tagFilterSelected(filter)">
                        <input type="checkbox" class="mr-10" :checked="filter.id === '' && !tagFilter.length ? true : containsTag(filter)">
                        <span class="block cursor-pointer whitespace-nowrap" x-text="filter.alias"></span>
                    </li>
                </template>
            `;
        },
        filterGroupsEnabled() {
            return this.availableFilterGroups.length && this.component.filters.tags;
        },
        tagFilterEnabled() {
            return this.availableTagFilters.length > 2 && this.component.filters.tags;
        },
        locationFilterEnabled() {
            return this.availableLocationFilters.length > 2 && this.component.filters.location;
        },
        typeFilterEnabled() {
            return this.availableTypeFilters.length > 2 && this.component.filters.type;
        },
        availableFilterGroups() {
            let filterGroups = [];

            this.activeForLocale
                .forEach(job => {
                    job.filters.forEach(filter => {
                        // get the filter group from the job filter since the pivot isn't available
                        const jobFilter = useRepo(JobFilter).where('filter_id', filter.id).first();

                        const groupId = jobFilter.filter_group_id;
                        let groupIndex = _findIndex(filterGroups, {id: groupId});

                        if (groupIndex < 0 && groupId) {
                            const filterGroup = useRepo(FilterGroup).find(groupId);
                            filterGroups.push({
                                id: groupId,
                                alias: filterGroup.titleLocale || this.$t('general.missing_translation'),
                                order: filterGroup.order,
                                filters: [],
                            });
                            groupIndex = _findIndex(filterGroups, {id: groupId});
                        }

                        // If the filter is not yet in the group, add it
                        const filterIndex = _findIndex(filterGroups[groupIndex].filters, {id: filter.id});
                        if (filterIndex < 0) {
                            this.addFilterToGroup(filterGroups, groupId, filter);
                        }
                    });
                });

            // Sort filter group filters
            filterGroups = filterGroups.filter(group => group.filters.length > 1);
            filterGroups.forEach(group => {
                group.filters = group.filters.sort((x, y) => {
                    if (x.alias && y.alias) {
                        return x.alias.toLowerCase().localeCompare(y.alias.toLowerCase());
                    }
                });
                let defaultFilter = {
                    id: '',
                    alias: ucfirst(this.$t('jobs.filter.all_types')),
                };
                group.filters.unshift(defaultFilter);
            });

            return filterGroups.sort((x, y) => x.order - y.order);
        },
        availableTagFilters() {
            let filters = [];

            // iterate through all jobs and unique filters
            this.activeForLocale
                .forEach(job => {
                    job.filters.forEach(filter => {
                        if (_findIndex(filters, filter) < 0) {
                            filters.push(filter);
                        }
                    });
                });

            if (filters.length) {
                // first sort
                filters = filters.sort((x, y) => {
                    if (x.title[this.locale] && y.title[this.locale]) {
                        return x.title[this.locale].toLowerCase().localeCompare(y.title[this.locale].toLowerCase());
                    }
                });

                // prepend default filter
                let filter = {
                    id: '',
                    title: {
                        [this.locale]: this.$t('jobs.filter.all_types'),
                    },
                };
                filters.unshift(filter);
            }

            return filters.filter(filter => filter.title[this.locale] !== '').map(item => ({
                id: item.id,
                alias: item.title[this.locale],
            }));
        },
        availableLocationFilters() {
            let locations = (this.allLocations && this.allLocations.length ? JSON.parse(JSON.stringify(this.allLocations)) : []);

            // No locations
            if (!locations.length) return [{ id: '', alias: this.$t('jobs.filter.locations') }];

            // map over the locations
            locations = [...locations.map(item => ({id: item.id, alias: item.alias?.[this.locale] || item.city}))
                // sort
                .sort((x, y) => x.alias.toLowerCase().localeCompare(y.alias.toLowerCase()))]

            // add to the beginning of the array
            locations.unshift({ id: '', alias: this.$t('jobs.filter.locations')})

            return locations.map(item => ({id: item.id, alias: item.alias || '* ' + this.$t('general.missing_translation')}))
        },
        availableTypeFilters() {
            return [
                {
                    id: '',
                    alias: this.$t('jobs.filter.fulltime-parttime'),
                },
                {
                    id: 'fulltime',
                    alias: this.$t('jobs.filter.fulltime'),
                },
                {
                    id: 'parttime',
                    alias: this.$t('jobs.filter.parttime'),
                },
            ];
        },
        customStyles() {
            return `.job-button.branded:hover {
                background-color: ${this.brandHSLA};
                color: ${this.brandContrastHEX};
            }`;
        },
        styleFilterText() {
            return responsive(this.preview.type, 'hidden sm:inline-block');
        },
        styleFilterIcon() {
            return responsive(this.preview.type, 'ml-8 sm:ml-0 ');
        },
        textSize() {
            return responsive(this.preview.type, 'text-body lg:text-heading-2');
        },
        textSizeSpontaneous() {
            return responsive(this.preview.type, 'text-body-sm lg:text-body');
        },
        buttonTextSize() {
            return responsive(this.preview.type, 'text-body-sm');
        },
        heavyJobButton() {
            return responsive(this.preview.type, 'mt-16 sm:mt-0');
        },
        heavyContainerWidthImg() {
            return responsive(this.preview.type, 'w-full md:w-5/6 lg:w-full');
        },
        heavyPadding() {
            return responsive(this.preview.type, 'p-40');
        },
        heavyPaddingWithImages() {
            return responsive(this.preview.type, 'py-24')
        },
        lightContainerWidth() {
            return responsive(this.preview.type, 'md:w-5/6');
        },
        lightFlexDirection() {
            return responsive(this.preview.type, 'flex-col mdplus:flex-row');
        },
        lightImageWidth() {
            return responsive(this.preview.type, 'w-full mdplus:w-5/12');
        },
        lightFlex() {
            return responsive(this.preview.type, 'sm:flex');
        },
        lightContentWidth() {
            return responsive(this.preview.type, 'mdplus:w-7/12');
        },
        lightButtonPadding() {
            return responsive(this.preview.type, 'inline-flex sm:flex-none sm:mt-0 sm:pl-24 md:pl-48');
        },
        lightFooterWidth() {
            return responsive(this.preview.type, 'md:w-1/2');
        },
        jobsSlug() {
            return _kebabCase(this.$t('companypage.menu.jobs'));
        },
        gridClasses() {
            return responsive(this.preview.type, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3')
        },
        // h-272 min-h-324 - for purge css
        gridItemHeight() {
            return responsive(this.preview.type, 'min-h-324');
        },
        gridItemWidth() {
            return responsive(this.preview.type, 'w-full');
        },
        gridItemHeightWithImage() {
            return responsive(this.preview.type, 'h-272 lg:h-340');
        },
        gridItemHeightWithoutImage() {
            return responsive(this.preview.type, 'h-164 md:h-200 lg:h-240');
        },
        gridItemWidthWithImage() {
            return responsive(this.preview.type, 'w-full xs:w-3/5 xs:mx-auto md:!w-full md:mx-0');
        },
        gridItemWidthWithoutImage() {
            return responsive(this.preview.type, 'w-full');
        },
        containerWidth() {

            if (this.style === 'grid') {
                return this.withImages ? this.heavyContainerWidthImg : 'w-full';
            }

            return this.withImages ? '' : this.lightContainerWidth;
        },
    },
    methods: {
        route,
        getJobTypes(job) {
            let jobTypes = [];

            job.jobTypes.forEach(jobType => {
                jobTypes.push(this.$t('job_parameters.type.' + jobType));
            });

            return jobTypes.join(', ');
        },
        tagFilterGroup(groupId) {
            return `
                <template x-for="filter in groupFilters(${groupId})">
                    <li class="mb-8 -mt-8 flex items-center pl-12 pr-16 py-4 hover:bg-primary-5" x-on:click="groupFilterSelected(${groupId}, filter)">
                        <input type="checkbox" class="mr-10" :checked="filter.id === '' && !Object.keys(groupFilter).length ? true : containsGroup(${groupId}, filter)">
                        <span class="block cursor-pointer whitespace-nowrap" x-text="filter.alias"></span>
                        <span x-text="containsTag(${groupId}, filter)"></span>
                    </li>
                </template>
            `;
        },
        addFilterToGroup(filterGroups, groupId, filter) {
            const groupIndex = _findIndex(filterGroups, {id: groupId});
            if (_findIndex(filterGroups[groupIndex].filters, filter) >= 0) {
                return;
            }

            filterGroups[groupIndex].filters.push({
                id: filter.id,
                alias: filter.titleLocale || this.$t('general.missing_translation'),
            });
        },
        getJobLocation(job) {
            return job.place_of_work;
        },

        filterForJob(job, index) {
            return '(tagFilter === \'\' || [' + job.filters.map(filter => '\'' + filter.id + '\'') + '].indexOf(tagFilter) !== -1)' +
                '&& (locationFilter === \'\' || ' + (job.location ? job.location.id : '\'\'') + ' === parseInt(locationFilter))' +
                '&& (typeFilter === \'\' || [' + job.type.map(type => '\'' + type + '\'') + '].indexOf(typeFilter) !== -1)' +
                `&& ${index} >= perPage * (page - 1) && ${index} < perPage * page`;
        },

        getButtonText(job) {
            if (job.extra && job.extra[this.locale] && (job.extra[this.locale].go_to_application_button || null)) {
                return job.extra[this.locale].go_to_application_button;
            }

            return this.$t('jobs.view_opening');
        },
    },
    mounted() {
        this.$emit('loaded');
    },
};
</script>
